import type {
    EnhancedVerificationRequest,
    ReinitiateVerificationRequest,
} from '@common/types/treefort';
import { Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import LoadingButton from '@/components/Buttons/LoadingButton';
import { invalidateAfterVerificationChange } from '@/queries/saidv';
import { reInitiateIDVerification } from '@/services/TreefortService';
import { successSnackbar } from '@/utils/snackbar';
import IdvModal from './IdvModal';
import { useIDVClientForm } from './useIDVClientForm';

interface Props {
    selectedRow: EnhancedVerificationRequest;
    prospectId?: string;
    onClose: () => void;
}

export default function ResendIDVModal({ selectedRow, prospectId, onClose }: Props) {
    const queryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState(false);
    const { clientUpdate, toggleCheckbox } = useIDVClientForm(selectedRow);

    const handleResendVerification = async () => {
        if (!selectedRow.clientRequestId) return;

        setIsLoading(true);
        try {
            const payload: ReinitiateVerificationRequest = {
                requestId: selectedRow.clientRequestId,
                isSecondaryIdRequired: clientUpdate.idVerification,
                isBankingVerificationRequired: clientUpdate.bankReview,
            };
            await reInitiateIDVerification(payload);
            await invalidateAfterVerificationChange(queryClient, prospectId);
            successSnackbar('ID Verification reinitiated successfully!');
            onClose();
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <IdvModal
            onClose={onClose}
            actions={
                <>
                    <Button variant="outlined" onClick={onClose}>
                        Cancel
                    </Button>
                    <LoadingButton
                        variant="contained"
                        loading={isLoading}
                        onClick={handleResendVerification}
                    >
                        Confirm
                    </LoadingButton>
                </>
            }
        >
            <Typography variant="h2">Resend ID Verification?</Typography>
            <Typography variant="body2" sx={{ marginTop: '8px' }}>
                Please verify the information below to proceed with ID verification.
            </Typography>
            <Box sx={{ marginTop: 3, marginBottom: 2 }}>
                <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1 }}>
                    {selectedRow.userName}
                </Typography>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    {selectedRow.email}
                </Typography>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                    {selectedRow.phoneNumber}
                </Typography>
            </Box>
            <Box>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={clientUpdate.idVerification}
                            onChange={() => toggleCheckbox('idVerification')}
                        />
                    }
                    label="Request Second ID"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={clientUpdate.bankReview}
                            onChange={() => toggleCheckbox('bankReview')}
                        />
                    }
                    label="Include Banking Verification"
                />
            </Box>
        </IdvModal>
    );
}
